import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useSetRecoilState, useRecoilValue } from 'recoil';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { AppList, AppWrapper, CircleButton } from '@capcenter/shared';
import * as AppState from './AppState';
import Navbar from './components/Navbar/Navbar';
import Sidebar from './components/Sidebar/Sidebar';
import Map from './components/Map/Map';
import PropertyDetails from './components/PropertyDetails/PropertyDetails';
import 'react-loading-skeleton/dist/skeleton.css';
import 'react-toastify/dist/ReactToastify.css';
import './utilities/fontAwesome';
import '@capcenter/shared-legacy/src/constants/GlobalStyles.scss';
import './App.scss';
import { NotFoundPage } from './components/NotFoundPage';

/**
 * Main control and distribution of core data lives here, coordinator
 * Where location and properties live and coordinated to rest of app
 * UserSearch --> Location<UserLocation> --> Properties
* 1) UserSearch = raw inputs to begin to determine location (iow: interface/contract for App-AAC)
* 2) Location = everything need to fetch Elastic Search Data (iow: interface/contract for App-GeoLocation)
* 3) Properties = Source of properties for the Map and List (iow: interface/contract for App-Map)
 * @returns react application
 */

export default function App() {
	const setGsPageSize = useSetRecoilState(AppState.gsPageSize);
	const setGsIsMobile = useSetRecoilState(AppState.gsIsMobile);
	const gsMobileView = useRecoilValue(AppState.gsMobileView);
	const gsIsMobile = useRecoilValue(AppState.gsIsMobile);
	const setGsScreenWidth = useSetRecoilState(AppState.gsScreenWidth);

	const ToastCloseButton = (props: {closeToast: any}) => (
		<CircleButton
			variant="flat"
			size="md"
			color="empty"
			className="toastify__custom-close-btn btn-close ms-2 mt-1"
			data-bs-dismiss="toast"
			aria-label="Close"
			icon="far fa-times mt-1"
			onClick={props.closeToast}
		/>
	);

	const MainApp = () => {
		function capitalizeFirstLetter(string: string) {
			return string.charAt(0).toUpperCase() + string.slice(1);
		}

    function capitalizeWords(input: string): string {
      return input.split(' ').map(word =>
          word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      ).join(' ');
    }

		const gsUserLocation = useRecoilValue(AppState.gsUserLocation);
		const cityOrCounty = gsUserLocation.city ? capitalizeWords(gsUserLocation.city) : gsUserLocation.county ? capitalizeWords(gsUserLocation.county) : '';
		const state = gsUserLocation.state ? gsUserLocation.state.toUpperCase() : '';
    const gsFilters = useRecoilValue(AppState.gsFilters)

		const meta = {
			title: gsFilters.openHouse?
        `Open Houses ${state ? `for homes in ${cityOrCounty && cityOrCounty + ', '}${state}` : 'Near Me'}` :
        `Zero Closing Costs homes for sale ${state && 'in '}${cityOrCounty && cityOrCounty + ', '}${state && state + ' '}`,
			description: gsFilters.openHouse?
        `Explore open houses for homes ${state ? `in ${cityOrCounty && cityOrCounty + ', '}${state}` : 'near me'}. View homes, schedule tours, and check out our 1% List Fee savings offer.`:
        `View homes for sale and schedule tours ${state ? `in ${cityOrCounty && cityOrCounty + ', '}${state}` : 'near me'}.`,
			siteName: 'CapCenter',
			image: 'https://www.capcenter.com/site-map/images/HomeSearch.jpg',
			keywords: 'best realtor, best realty, best real estate agent, best buyers agent, best listing agent, best mortgage lender, best home loans, top realtor, top realty'
		};
		return (
			<React.Fragment>
				<Helmet>
					<title className='text-capitalize'>{meta.title}</title>

					<meta name="description" content={meta.description} />
					<meta name="keywords" content={meta.keywords} />

					<meta itemProp="name" content={meta.title} />
					<meta itemProp="description" content={meta.description} />
					<meta itemProp="keywords" content={meta.keywords} />
					<meta itemProp="image" content={meta.image} />
					{/* FACEBOOK */}
					<meta property="og:title" content={meta.title} />
					<meta property="og:site_name" content={meta.siteName} />
					<meta property="og:description" content={meta.description} />
					<meta property="og:url" content={'https://www.capcenter.com' + AppList.homeSearch.url} />
					<meta property="og:image" content={meta.image} />

					{/* TWITTER */}
					<meta name="twitter:title" content={meta.siteName} />
					<meta name="twitter:description" content={meta.description} />
					<meta name="twitter:image" content={meta.image} />
					<meta name="twitter:site" content="@CapCenterLLC" />
					<meta name="twitter:creator" content="@CapCenterLLC" />
					<meta name="twitter:card" content="summary_large_image" />

					<meta name="robots" content="index, follow" />
				</Helmet>
				<Navbar />
				<div className="home-search__main-content d-flex">
					<React.Fragment>
						<ToastContainer closeButton={ToastCloseButton} autoClose={5000} theme="colored" limit={3} enableMultiContainer newestOnTop containerId="filterWarningToastContainer" />
						<Sidebar hidden={gsIsMobile && gsMobileView === 1} />
						<Map hidden={gsIsMobile && gsMobileView === 0} />
					</React.Fragment>
				</div>
			</React.Fragment>
		);
	};

	useEffect(() => {
		handleWindowSize();
		window.addEventListener('resize', handleWindowSize, { passive: true });  // fire on window resize
		return () => window.removeEventListener('resize', handleWindowSize);  // clean up event listener
	}, []);

	function handleWindowSize() {
		const screenWidth = window.innerWidth;
		setGsScreenWidth(screenWidth);

		if (screenWidth < 992) {
			setGsIsMobile(true);
			setGsPageSize(50);
		} else {
			setGsIsMobile(false);
			setGsPageSize(100);
		}
	}


	return (
		<React.Fragment>
			<AppWrapper height100 noFooter>
				<div id="homeSearchApp" style={{padding: '10px'}} className="container-fluid h-100 position-relative overflow-hidden">
					<BrowserRouter basename="/homes">
						<Routes>
							<Route path="/" element={<MainApp />} />
							<Route path="/location/*" element={<MainApp />} />
							<Route path="/filters/*" element={<MainApp />} />
							<Route path="/sort/*" element={<MainApp />} />
							<Route path="/property-details/:propertyAddress/:propertyId" element={<PropertyDetails />} />
							<Route path="/property-details/:propertyAddress" element={<PropertyDetails />} />
							<Route path="/real-estate/:propertyAddress" element={<PropertyDetails />} />
							<Route path="*" element={<NotFoundPage/>} />
						</Routes>
					</BrowserRouter>
				</div>
			</AppWrapper>
		</React.Fragment>
	);
}
